import React from 'react';
import { PropTypes } from 'prop-types';
import { Controller, get } from 'react-hook-form';
import { Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import getFormControllerRules from '../utils/getFormControllerRules';
import useRemoveSpaces from '../utils/removeSpaces';

/**
 * Reusable DatePicker based Select dropdown for use with react-hook-form.
 *
 * @param {object} control - React hook form control.
 * @param {object} formErrors - React hook form formErrors.
 * @param {string} label - label to be used of the field as well as test id and error messages.
 * @param {string} formFieldId - id to be used with react-hook-form.
 * @param {boolean} required - is the field required?
 * @param {boolean} readonly - is the field read only?
 * @param {DatePickerProps} rest - also accepts all DatePicker props.
 * @param {string} testId - alternative testId label to overwrite label.
 * @param {validationOptions} validationOptions - react hook form validation options.
 * @param {func} onAdditionalMethod - Additional method when onChange is fired.
 */
const FormDatePicker = ({
  control,
  formErrors,
  label,
  formFieldId,
  required,
  readonly,
  testId,
  validationOptions,
  helperText,
  showInlineErrors = true,
  onAdditionalMethod,
  ...rest
}) => {
  const testIdMemo = useRemoveSpaces(testId ?? `${ label }_Field`);
  const rules = getFormControllerRules(label, required, validationOptions);
  const modifiedHelperText = (showInlineErrors && get(formErrors, formFieldId)?.message) || helperText;

  return (
    <Controller
      name={formFieldId}
      control={control}
      rules={rules}
      render={({ field }) => (
        <Stack data-testid={testIdMemo}>
          <DatePicker
            {...field}
            {...rest}
            disabled={readonly}
            label={label}
            slotProps={{
              textField: {
                required,
                helperText: modifiedHelperText,
                error: !!get(formErrors, formFieldId)
              }
            }}
            onChange={(date) => {
              field.onChange(date ?? null);
              // eslint-disable-next-line no-unused-expressions
              onAdditionalMethod && onAdditionalMethod(date);
            }}
          />
        </Stack>
      )}
    />
  );
};

FormDatePicker.propTypes = {
  control: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  label: PropTypes.string.isRequired,
  formFieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  testId: PropTypes.string,
  validationOptions: PropTypes.object,
  helperText: PropTypes.string,
  showInlineErrors: PropTypes.bool,
  onAdditionalMethod: PropTypes.func
};

FormDatePicker.defaultProps = {
  formErrors: null,
  required: false,
  readonly: false,
  testId: null,
  validationOptions: {},
  helperText: null,
  showInlineErrors: true,
  onAdditionalMethod: null
};

export default FormDatePicker;
